import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  TextField,
  Divider,
  Fade,
  Zoom,
  LinearProgress,
} from '@mui/material';
import { UploadFile as UploadFileIcon, CheckCircle } from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import { useAccessToken } from '@features/user';
import { v4 as uuidv4 } from 'uuid';
import APIService from '../../services/APIService';
import { useTemplateBuilder } from './TemplateBuilderContext';

// Keyframe animations
const fadeOutUp = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-30px);
  }
`;

const scaleUp = keyframes`
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 200, 83, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(0, 200, 83, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 200, 83, 0);
  }
`;

// Styled components
const DropZone = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  textAlign: 'center',
  backgroundColor: theme.palette.background.default,
  cursor: 'pointer',
  transition: 'border-color 0.3s, background-color 0.3s',
  marginBottom: theme.spacing(2),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

const SuccessIcon = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.common.white,
  animation: `${pulse} 1.5s ease-in-out`,
}));

interface FileUploadProps {
  title?: string;
  description?: string;
  acceptedFileTypes?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  title = 'Import your note template',
  description = "Paste text or drag a file to import. We'll turn it into a JotPsych template for you.",
  acceptedFileTypes = '.pdf,.docx,.txt,.jpg,.jpeg,.png',
}) => {
  // Get access token from user context
  const { getAccessToken } = useAccessToken();
  const { handleFileUploadStarted, processingError, sessionId } = useTemplateBuilder();

  // Simple state management using useState hooks
  const [importText, setImportText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoadingBar, setShowLoadingBar] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [fadeOutContent, setFadeOutContent] = useState(false);

  // References
  const fileInputRef = useRef<HTMLInputElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileUpload(files[0]);
    }
  };

  const handleClickDropZone = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFileUpload(files[0]);
    }
  };

  // Handle file upload with animation sequence
  const handleFileUpload = async (file: File) => {
    try {
      setError(null);

      // Check file size - prevent files larger than 1MB
      const maxSizeInBytes = 1 * 1024 * 1024; // 1MB
      if (file.size > maxSizeInBytes) {
        setError(`File size exceeds the 1MB limit. Please upload a smaller file.`);
        return;
      }

      // Show loading bar immediately
      setShowLoadingBar(true);
      setIsSubmitting(true);

      // Get access token
      const accessToken = await getAccessToken();

      // Step 1: Get the presigned URL for S3 upload
      const contentType = file.type || 'application/octet-stream';
      const getUploadUrlResponse = await APIService.makeAPIGetRequest({
        requestString: '/templatebuilder/getFileUploadUrl',
        params: {
          content_type: contentType,
          expiration: '3600',
        },
        accessToken: accessToken,
      });

      if (!getUploadUrlResponse.ok) {
        throw new Error(`Failed to get upload URL: ${getUploadUrlResponse.error?.message}`);
      }

      const { upload_url, s3_object_id } = getUploadUrlResponse.value;

      // Step 2: Upload the file to S3 using the presigned URL
      const uploadResponse = await fetch(upload_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': contentType,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload file to S3: ${uploadResponse.statusText}`);
      }

      // Step 3: Send the upload request to start processing
      const uploadTemplateResponse = await APIService.makeAPIPostRequest({
        requestString: '/templatebuilder/upload',
        accessToken: accessToken,
        body: {
          template_builder_session_id: sessionId,
          s3_object_id,
          file_type: contentType,
        },
      });

      if (!uploadTemplateResponse.ok) {
        throw new Error(`Failed to process template: ${uploadTemplateResponse.error?.message}`);
      }

      const taskId = uploadTemplateResponse.value.task_id;

      // Log the file
      console.log(`Processing file: ${file.name} with task ID: ${taskId}`);

      // Start animation sequence
      setFadeOutContent(true);

      // After content fades out, show success animation
      setTimeout(() => {
        setShowLoadingBar(false);
        setShowSuccessAnimation(true);

        // After success animation, call the callback
        setTimeout(() => {
          handleFileUploadStarted(taskId);
        }, 800);
      }, 400);
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(`Failed to upload file: ${err instanceof Error ? err.message : String(err)}`);
      setIsSubmitting(false);
      setShowLoadingBar(false);
    }
  };

  // Handle text import with animation sequence
  const handleTextImport = async () => {
    if (!importText.trim()) {
      setError('Please enter some content to import.');
      return;
    }

    try {
      setError(null);
      setIsSubmitting(true);
      setShowLoadingBar(true);

      // Get access token
      const accessToken = await getAccessToken();

      // Send the text content for processing
      const uploadTemplateResponse = await APIService.makeAPIPostRequest({
        requestString: '/templatebuilder/upload',
        accessToken: accessToken,
        body: {
          template_builder_session_id: sessionId,
          pasted_text: importText,
        },
      });

      if (!uploadTemplateResponse.ok) {
        throw new Error(`Failed to process template: ${uploadTemplateResponse.error?.message}`);
      }

      const taskId = uploadTemplateResponse.value.task_id;

      // Log the text
      console.log(`Processing text of length: ${importText.length} with task ID: ${taskId}`);

      // Start animation sequence
      setFadeOutContent(true);

      // After content fades out, show success animation
      setTimeout(() => {
        setShowLoadingBar(false);
        setShowSuccessAnimation(true);

        // After success animation, call the callback
        setTimeout(() => {
          handleFileUploadStarted(taskId);
        }, 800);
      }, 400);
    } catch (err) {
      console.error('Error importing text:', err);
      setError(`Failed to import text: ${err instanceof Error ? err.message : String(err)}`);
      setIsSubmitting(false);
      setShowLoadingBar(false);
    }
  };

  // Determine content type from file
  const getContentType = (file: File): string => {
    return file.type || 'application/octet-stream';
  };

  // Add useEffect to watch for errors from the context
  useEffect(() => {
    if (processingError) {
      // If there's a processing error from the context, show it
      setError(processingError);
      setShowSuccessAnimation(false);
      setFadeOutContent(false);
      setIsSubmitting(false);
      setShowLoadingBar(false);
    }
  }, [processingError]);

  return (
    <Paper
      ref={paperRef}
      elevation={0}
      sx={{
        p: 4,
        borderRadius: 2,
        width: '100%',
        maxWidth: 700,
        mx: 'auto',
        position: 'relative',
        height: showSuccessAnimation ? 300 : 'auto',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: showSuccessAnimation ? 'center' : 'flex-start',
        transition: 'height 0.3s ease',
      }}
    >
      {/* Loading bar */}
      {showLoadingBar && (
        <Box sx={{ width: '100%', position: 'absolute', top: 0, left: 0 }}>
          <LinearProgress />
        </Box>
      )}

      {/* Success animation */}
      {showSuccessAnimation ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            animation: `${scaleUp} 0.5s ease-out forwards`,
          }}
        >
          <SuccessIcon>
            <CheckCircle fontSize="large" />
          </SuccessIcon>
          <Typography variant="h6" sx={{ mt: 2, color: 'success.main', fontWeight: 600 }}>
            Upload Successful!
          </Typography>
        </Box>
      ) : (
        <Fade in={!fadeOutContent} timeout={400}>
          <Box
            sx={{
              width: '100%',
              animation: fadeOutContent ? `${fadeOutUp} 0.4s forwards` : 'none',
            }}
          >
            <Typography variant="h5" gutterBottom align="center">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
              {description}
            </Typography>

            {/* File upload area */}
            <DropZone
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleClickDropZone}
              sx={{
                borderColor: isDragging ? 'primary.main' : 'divider',
                backgroundColor: isDragging ? 'action.hover' : 'background.default',
              }}
            >
              <input
                type="file"
                ref={fileInputRef}
                accept={acceptedFileTypes}
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
                disabled={isSubmitting}
              />
              <UploadFileIcon color="primary" sx={{ fontSize: 60, mb: 2, opacity: 0.7 }} />
              <Typography variant="h6" gutterBottom>
                Drag & Drop File
              </Typography>
              <Typography variant="body2" color="text.secondary">
                or click to browse
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                Accepted formats: {acceptedFileTypes.replace(/\./g, '').replace(/,/g, ', ')}
              </Typography>
            </DropZone>

            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>

            {/* Text input area */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Paste text content
              </Typography>
              <TextField
                multiline
                rows={5}
                fullWidth
                placeholder="Paste your text content here..."
                value={importText}
                onChange={e => setImportText(e.target.value)}
                disabled={isSubmitting}
              />
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                fullWidth
                onClick={handleTextImport}
                disabled={isSubmitting || !importText.trim()}
              >
                Import Text
              </Button>
            </Box>

            {/* Error message */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </Fade>
      )}
    </Paper>
  );
};

export default FileUpload;
